// Ruta /leaderboard — top partidas ganadas (Sprint 6).
//
// Consume GET /api/v1/leaderboard. Si el backend no responde, muestra
// fallback con mensaje explicativo (modo offline o backend no levantado).

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getLeaderboard, ApiException } from '../api/client';
import type { LeaderboardEntry } from '@regicide/shared';

export const Route = createFileRoute('/leaderboard')({
  component: LeaderboardPage,
});

function LeaderboardPage() {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<'time' | 'moves' | 'lives'>('time');

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setError(null);
    getLeaderboard({ sort, limit: 20 })
      .then((res) => {
        if (!cancelled) setEntries(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiException) {
          setError(err.message);
        } else {
          setError('No se pudo cargar el leaderboard.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sort]);

  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="border-b border-border bg-bg-alt/40">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/" className="text-sm text-text-muted transition hover:text-gold">
            ← Inicio
          </Link>
          <h1 className="font-display text-lg text-gold">Leaderboard</h1>
          <Link to="/play" className="text-sm text-gold transition hover:text-gold-soft">
            Jugar
          </Link>
        </div>
      </div>

      <section className="mx-auto max-w-5xl p-4 md:p-6">
        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm text-text-muted">Ordenar por:</label>
          <div className="flex gap-2" role="group" aria-label="Ordenar leaderboard">
            {(['time', 'moves', 'lives'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSort(s)}
                aria-pressed={sort === s}
                className={[
                  'rounded-md px-3 py-1 text-sm transition',
                  sort === s
                    ? 'bg-gold text-bg'
                    : 'border border-border text-text-muted hover:border-gold hover:text-gold',
                ].join(' ')}
              >
                {s === 'time' ? 'Tiempo' : s === 'moves' ? 'Turnos' : 'Vidas'}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div
            className="rounded-xl border border-blood bg-blood/10 p-4 text-blood"
            role="alert"
            data-testid="leaderboard-error"
          >
            <strong>No se pudo cargar el leaderboard.</strong>
            <p className="mt-1 text-sm text-text-muted">{error}</p>
            <p className="mt-2 text-xs text-text-muted">
              Tip: si estás en local, asegurate de tener el backend corriendo con{' '}
              <code>docker compose up -d --build</code>.
            </p>
          </div>
        )}

        {entries === null && !error && (
          <p className="text-text-muted" data-testid="leaderboard-loading">
            Cargando…
          </p>
        )}

        {entries && entries.length === 0 && (
          <div
            className="rounded-xl border border-dashed border-border bg-bg-alt/40 p-8 text-center"
            data-testid="leaderboard-empty"
          >
            <p className="text-text-muted">Todavía no hay partidas en el leaderboard.</p>
            <p className="mt-1 text-xs text-text-muted">Sé el primero en terminar una partida.</p>
          </div>
        )}

        {entries && entries.length > 0 && (
          <ol
            className="grid gap-2"
            data-testid="leaderboard-list"
            aria-label="Leaderboard de partidas ganadas"
          >
            {entries.map((entry, idx) => (
              <motion.li
                key={entry.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: idx * 0.02 }}
                className="flex items-center justify-between rounded-xl border border-border bg-bg-card p-3"
                data-testid={`leaderboard-entry-${idx}`}
              >
                <div className="flex items-center gap-4">
                  <span className="font-display text-2xl text-gold">#{idx + 1}</span>
                  <div>
                    <p className="font-mono text-sm text-text">Seed: {entry.seed}</p>
                    <p className="text-xs text-text-muted">
                      {new Date(entry.endedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <dl className="flex gap-4 text-right text-sm">
                  <div>
                    <dt className="text-xs uppercase text-text-muted">Tiempo</dt>
                    <dd className="font-mono text-text">{formatDuration(entry.durationMs)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-text-muted">Turnos</dt>
                    <dd className="font-mono text-text">{entry.movesCount}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-text-muted">Vidas</dt>
                    <dd className="font-mono text-gold">{entry.livesRemaining}</dd>
                  </div>
                </dl>
              </motion.li>
            ))}
          </ol>
        )}
      </section>
    </main>
  );
}

function formatDuration(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  return `${min}:${s.toString().padStart(2, '0')}`;
}
